import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { format, isSameDay } from 'date-fns';
import type { CalendarEventGroup } from '../../types';
import type { DelegateCalendarEventRow } from '../../utils/executiveCalendarDelegatedService';
import { getEventGroupId, isGroupedEvent } from '../../utils/executiveCalendarDelegatedService';
import {
  dayLayoutEventKey,
  layoutDayEvents,
  type DayEventCluster,
  type DayLayoutResult,
} from '../../utils/executiveCalendarDayLayout';

type EacWeekPanelProps = {
  panelMonday: Date;
  weekIdx: number;
  panelDays: Date[];
  eventsByDate: Map<string, DelegateCalendarEventRow[]>;
  calendarColors: Map<string, string>;
  eventIdToGroup: Map<string, CalendarEventGroup>;
  dayBodyPx: number;
  hourCellPx: number;
  nowTopPct: number | null;
  calendarNow: Date;
  hourTicks: number[];
  dayStartHour: number;
  dayEndHour: number;
  isActive: boolean;
  registerVScrollEl: (weekIdx: number, node: HTMLDivElement | null) => void;
  onVerticalScroll: (weekIdx: number, scrollTop: number) => void;
  onDayBodyClick: (e: React.MouseEvent<HTMLDivElement>, day: Date) => void;
  onPointerDownDrag: (e: React.PointerEvent, ev: DelegateCalendarEventRow) => void;
  onPointerMoveDrag: (e: React.PointerEvent) => void;
  endDrag: (e: React.PointerEvent) => void;
  onEventHover: (
    ev: DelegateCalendarEventRow,
    cluster: DayEventCluster,
    anchorRect: DOMRect,
    isShort: boolean,
  ) => void;
  onEventHoverEnd: () => void;
};

const EacWeekPanel = memo(function EacWeekPanel({
  panelMonday,
  weekIdx,
  panelDays,
  eventsByDate,
  calendarColors,
  eventIdToGroup,
  dayBodyPx,
  hourCellPx,
  nowTopPct,
  calendarNow,
  hourTicks,
  dayStartHour,
  dayEndHour,
  isActive,
  registerVScrollEl,
  onVerticalScroll,
  onDayBodyClick,
  onPointerDownDrag,
  onPointerMoveDrag,
  endDrag,
  onEventHover,
  onEventHoverEnd,
}: EacWeekPanelProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const onVerticalScrollRef = useRef(onVerticalScroll);
  onVerticalScrollRef.current = onVerticalScroll;

  const bindScrollRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollRef.current = node;
      registerVScrollEl(weekIdx, node);
    },
    [weekIdx, registerVScrollEl],
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handler = () => onVerticalScrollRef.current(weekIdx, el.scrollTop);
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, [weekIdx, isActive]);

  const dayLayouts = useMemo(() => {
    const map = new Map<string, DayLayoutResult>();
    if (!isActive) return map;
    for (const d of panelDays) {
      const key = format(d, 'yyyy-MM-dd');
      const evs = eventsByDate.get(key);
      if (evs && evs.length > 0) {
        map.set(key, layoutDayEvents(evs, dayStartHour, dayEndHour));
      }
    }
    return map;
  }, [panelDays, eventsByDate, isActive, dayStartHour, dayEndHour]);

  const showNow = nowTopPct !== null && panelDays.some(d => isSameDay(d, calendarNow));

  return (
    <section
      className={`eac-week-panel${isActive ? ' is-active' : ' is-stub'}`}
      aria-label={`Week of ${format(panelMonday, 'MMMM d, yyyy')}`}
      data-week-idx={weekIdx}
    >
      <div className="eac-week-head-row">
        <div className="eac-corner-spacer" />
        {panelDays.map(d => (
          <div
            key={d.toISOString()}
            className={`eac-day-head${isSameDay(d, calendarNow) ? ' is-today' : ''}`}
          >
            {format(d, 'EEE')}
            <br />
            {format(d, 'd')}
          </div>
        ))}
      </div>
      <div className="eac-week-body-scroll" ref={bindScrollRef}>
        <div className="eac-week-body-row eac-zoomable">
          <div className="eac-times-col">
            {hourTicks.slice(0, -1).map(h => (
              <div key={h} className="eac-time-cell" style={{ height: hourCellPx }}>
                {h % 12 || 12}
                {h < 12 ? 'am' : 'pm'}
              </div>
            ))}
            {isActive && showNow && (
              <div className="eac-now-time-marker" style={{ top: `${nowTopPct}%` }} aria-hidden>
                {format(calendarNow, 'h:mm a')}
              </div>
            )}
          </div>
          <div className="eac-days-cols">
            {panelDays.map(d => {
              const dayKey = format(d, 'yyyy-MM-dd');
              const isToday = isSameDay(d, calendarNow);
              const dayLayout = dayLayouts.get(dayKey);
              return (
                <div
                  key={`col-${d.toISOString()}`}
                  className={`eac-day-body eac-day-body-hour-grid${isToday ? ' is-today' : ''}`}
                  style={{ height: dayBodyPx }}
                  onClick={isActive ? e => onDayBodyClick(e, d) : undefined}
                  title={isActive ? 'Click to add event or build trip' : undefined}
                >
                  {isActive && isToday && nowTopPct !== null && (
                    <div
                      className="eac-now-line"
                      style={{ top: `${nowTopPct}%` }}
                      aria-label={`Current time ${format(calendarNow, 'h:mm a')}`}
                    />
                  )}
                  {isActive &&
                    dayLayout &&
                    (eventsByDate.get(dayKey) || []).map(ev => {
                      const layout = dayLayout.layouts.get(dayLayoutEventKey(ev));
                      if (!layout) return null;
                      const cluster = dayLayout.clusters[layout.clusterId];
                      const bg = calendarColors.get(ev.calendarId) || '#bfdbfe';
                      const grouped = isGroupedEvent(ev);
                      const isTravel = ev.extendedPropertiesPrivate?.lctfSegmentType === 'travel';
                      const groupId = getEventGroupId(ev);
                      const group = groupId ? eventIdToGroup.get(ev.id) : undefined;
                      const tripHint = group
                        ? `Trip: ${group.segments.length} linked events`
                        : ev.title;
                      return (
                        <div
                          key={`${ev.calendarId}-${ev.id}`}
                          className={`eac-event-pill${grouped ? ' is-grouped' : ''}${isTravel ? ' is-group-travel' : ''}`}
                          style={{
                            top: `${layout.topPct}%`,
                            height: `${layout.heightPct}%`,
                            left: `calc(${layout.leftPct}% + 2px)`,
                            width: `calc(${layout.widthPct}% - 4px)`,
                            background: `${bg}cc`,
                          }}
                          title={tripHint}
                          onPointerDown={e => onPointerDownDrag(e, ev)}
                          onPointerMove={onPointerMoveDrag}
                          onPointerUp={endDrag}
                          onPointerCancel={endDrag}
                          onMouseEnter={e =>
                            onEventHover(
                              ev,
                              cluster,
                              e.currentTarget.getBoundingClientRect(),
                              layout.isShort,
                            )
                          }
                          onMouseLeave={onEventHoverEnd}
                        >
                          {grouped && <span className="eac-group-badge">Trip · </span>}
                          <strong>{ev.title}</strong>
                          {ev.location && <div style={{ opacity: 0.85 }}>{ev.location}</div>}
                        </div>
                      );
                    })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
});

export default EacWeekPanel;
